// install-reconcile-triggers.ts — shared "wake the catchup chain" wiring
// for SnapshotProvider + LedgerProvider. Extracted as a pure helper so the
// installation can be unit-tested in pure Node (no jsdom dep) and so the
// two providers can't drift from each other.
//
// Per ADR 011 §5 / ADR 017 §5 the realtime client should fire the per-
// topic catchup on FIVE triggers:
//
//   1. Initial mount             — owned by the store (`subscribe()`)
//   2. Broadcast gap detection   — owned by the store (`handleUpdate` /
//                                  `handleHint`)
//   3. Transport reconnect       — installed here via
//                                  `subscriber.onConnectionChange`
//   4. Browser wake events       — installed here:
//                                  `window.focus`, `document.visibilitychange`,
//                                  `window.online`
//   5. Periodic safety-net tick  — installed here via `setInterval`
//
// This helper covers (3)–(5). It is SSR-safe: when called without a
// browser-like environment (no `window` / `document`), it installs no
// listeners and returns a no-op disposer.

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import type { Unsubscribe } from '../../types/realtime/types';

/**
 * Minimal `window` shape used by {@link installReconcileTriggers}.
 * Accepting an injected `windowLike` instead of reaching for
 * `globalThis.window` directly lets the helper run in pure-Node tests
 * without jsdom.
 */
export interface WindowLike {
    addEventListener(
        event: 'focus' | 'online',
        listener: () => void,
    ): void;
    removeEventListener(
        event: 'focus' | 'online',
        listener: () => void,
    ): void;
}

/**
 * Minimal `document` shape used by {@link installReconcileTriggers}.
 * The provider only inspects `visibilityState` (to filter
 * visibilitychange events to "becoming visible"), so a stub with that
 * property is sufficient.
 */
export interface DocumentLike {
    addEventListener(
        event: 'visibilitychange',
        listener: () => void,
    ): void;
    removeEventListener(
        event: 'visibilitychange',
        listener: () => void,
    ): void;
    readonly visibilityState: 'visible' | 'hidden' | 'prerender';
}

/**
 * Timers the helper uses for the periodic safety-net tick AND the
 * reconcile throttle cooldown. Defaults to the `globalThis` timers when
 * omitted. Tests can pass `vi.useFakeTimers()`-friendly stubs.
 */
export interface TimerLike {
    setInterval(handler: () => void, ms: number): unknown;
    clearInterval(handle: unknown): void;
    setTimeout(handler: () => void, ms: number): unknown;
    clearTimeout(handle: unknown): void;
}

/**
 * Default minimum spacing (ms) between `reconcileAll` sweeps (BUG-1175).
 *
 * `reconcileAll` fires a per-topic catch-up (an uncached DB read) for
 * EVERY active snapshot/ledger topic. Each of the five triggers
 * (focus / visibilitychange / online / transport-reconnect / interval)
 * used to call it with no rate limit, so a flapping realtime socket
 * (repeated `CHANNEL_ERROR`/`TIMED_OUT` → `SUBSCRIBED`) turned every
 * reconnect into a full sweep — ~600k catch-up reads / 72h on dev,
 * exhausting the shared Supavisor 200-client pool (EMAXCONN).
 *
 * A leading+trailing throttle bounds ANY trigger storm to ≤1 sweep per
 * window: the first trigger fires immediately (no added latency in the
 * common single-reconnect case), and further triggers inside the window
 * collapse to a single trailing sweep. `reconcileAll` is a safety net for
 * broadcasts missed while disconnected — genuine live updates still apply
 * instantly via the un-throttled `handleUpdate`/`handleHint` broadcast
 * path — so a ≤window delay on gap recovery is harmless. Tunable.
 */
export const RECONCILE_THROTTLE_MS = 15_000;

export interface InstallReconcileTriggersOptions {
    /** Single "fire catchup for every active topic" entry point. */
    reconcileAll: () => void;
    /** Transport subscriber — `onConnectionChange` is optional. */
    subscriber: RealtimeSubscriber;
    /**
     * Periodic safety-net tick (ms). `0` disables the interval — useful
     * in tests that drive timing manually. Default in callers is 120_000
     * (2 minutes — BL-563).
     */
    intervalMs: number;
    /**
     * Minimum spacing (ms) between `reconcileAll` sweeps (BUG-1175).
     * `<= 0` disables the throttle (every trigger fires immediately — the
     * pre-BUG-1175 behavior, used by tests that assert per-trigger firing).
     * Defaults to {@link RECONCILE_THROTTLE_MS}.
     */
    reconcileThrottleMs?: number;
    /**
     * Override the event source for focus / online. Defaults to
     * `globalThis.window` when running in a browser; SSR contexts pass
     * nothing and the helper short-circuits to a no-op disposer.
     */
    windowLike?: WindowLike;
    /** Override the visibilitychange source. Same SSR rules as `windowLike`. */
    documentLike?: DocumentLike;
    /** Override the timer source. Defaults to `globalThis`. */
    timers?: TimerLike;
}

/**
 * Install the standard set of "fire `reconcileAll` on wake / reconnect /
 * tick" triggers shared by SnapshotProvider and LedgerProvider.
 *
 * Returns a disposer that removes every listener and clears the interval.
 * SSR-safe — returns a no-op disposer when no `windowLike` / `documentLike`
 * is available.
 *
 * @sideEffect Adds `focus` + `online` listeners on `windowLike`, a
 *   `visibilitychange` listener on `documentLike`, an
 *   `onConnectionChange` handler on the subscriber (when supported), and a
 *   `setInterval` tick (when `intervalMs > 0`).
 */
export function installReconcileTriggers(
    opts: InstallReconcileTriggersOptions,
): Unsubscribe {
    const win = opts.windowLike ?? getGlobalWindow();
    const doc = opts.documentLike ?? getGlobalDocument();
    const timers = opts.timers ?? getGlobalTimers();

    // SSR / non-DOM environments — nothing to install. The store still runs
    // its initial-mount catchup; the wake/reconnect path simply doesn't
    // exist on the server side.
    if (!win) return () => {};

    const throttleMs = opts.reconcileThrottleMs ?? RECONCILE_THROTTLE_MS;

    // BL-2342 (ADR 066 — Offline Mode § 6.2): while the transport is suspended
    // for offline mode, no trigger runs a sweep — not a wake event, not the
    // interval, not a trailing sweep that comes due. Leaving offline mode
    // reconnects the socket, and its 'reconnected' is the one catch-up.
    const sweep = () => {
        if (opts.subscriber.isSuspended?.()) return;
        opts.reconcileAll();
    };

    // Leading+trailing throttle (BUG-1175). `cooldownHandle === undefined`
    // means idle → the next trigger is a leading edge that fires
    // `reconcileAll` immediately (no added latency on a lone reconnect).
    // While a cooldown is open, extra triggers only set `trailingPending`,
    // collapsing a storm into ONE trailing sweep when the window elapses; a
    // continuing storm re-arms the cooldown so the rate stays ≤1 sweep/window.
    let cooldownHandle: unknown | undefined;
    let trailingPending = false;

    const armCooldown = () => {
        cooldownHandle = timers.setTimeout(() => {
            if (trailingPending) {
                trailingPending = false;
                sweep();
                armCooldown();
            } else {
                cooldownHandle = undefined;
            }
        }, throttleMs);
    };

    const reconcile =
        throttleMs <= 0
            ? sweep
            : () => {
                  if (opts.subscriber.isSuspended?.()) return;
                  if (cooldownHandle === undefined) {
                      sweep();
                      armCooldown();
                  } else {
                      trailingPending = true;
                  }
              };

    const onVisibilityChange = doc
        ? () => {
              if (doc.visibilityState === 'visible') {
                  reconcile();
              }
          }
        : undefined;

    win.addEventListener('focus', reconcile);
    win.addEventListener('online', reconcile);
    if (doc && onVisibilityChange) {
        doc.addEventListener('visibilitychange', onVisibilityChange);
    }

    // Transport-level reconnect (BL-563). Subscriber may not implement
    // this — treat absence as "no signal available."
    const offConnectionChange =
        opts.subscriber.onConnectionChange?.((event) => {
            if (event === 'reconnected') {
                reconcile();
            }
        }) ?? (() => {});

    const intervalHandle =
        opts.intervalMs > 0
            ? timers.setInterval(reconcile, opts.intervalMs)
            : undefined;

    let disposed = false;
    return () => {
        if (disposed) return;
        disposed = true;
        win.removeEventListener('focus', reconcile);
        win.removeEventListener('online', reconcile);
        if (doc && onVisibilityChange) {
            doc.removeEventListener('visibilitychange', onVisibilityChange);
        }
        offConnectionChange();
        if (intervalHandle !== undefined) {
            timers.clearInterval(intervalHandle);
        }
        // Clear any open throttle cooldown so a trailing sweep can't fire
        // after the provider unmounts (BUG-1175).
        if (cooldownHandle !== undefined) {
            timers.clearTimeout(cooldownHandle);
        }
    };
}

function getGlobalWindow(): WindowLike | undefined {
    // typeof check avoids a ReferenceError in environments where `window`
    // isn't declared (pure Node test runner).
    return typeof window === 'undefined'
        ? undefined
        : (window as unknown as WindowLike);
}

function getGlobalDocument(): DocumentLike | undefined {
    return typeof document === 'undefined'
        ? undefined
        : (document as unknown as DocumentLike);
}

function getGlobalTimers(): TimerLike {
    return {
        setInterval: (handler, ms) =>
            globalThis.setInterval(handler, ms) as unknown,
        clearInterval: (handle) =>
            globalThis.clearInterval(handle as ReturnType<typeof setInterval>),
        setTimeout: (handler, ms) =>
            globalThis.setTimeout(handler, ms) as unknown,
        clearTimeout: (handle) =>
            globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
    };
}
